import { assignIn, cloneDeep, filter, find, first, has, includes, isArray, isEmpty, reduce, remove } from 'lodash';
import storage from '@api/storage';
import util from '@api/util';
import watUtil from '@api/wat/watUtil';
const _ = { assignIn, cloneDeep, filter, find, first, has, includes, isArray, isEmpty, reduce, remove };

const GUEST_NAME = 'Guest';
const DISPLAY_MODE_SYSTEM = 'system';
const DISPLAY_MODE_LIGHT = 'light';
const DISPLAY_MODE_DARK = 'dark';
const DISPLAY_PCT = 0.9;

const ROLE_FACULTY_STAFF = 'hawaii.edu:store:uhims:general:faculty-staff';
const ROLE_GCN_COMPLIANT = 'hawaii.edu:store:uhims:general:gcn-compliant';
const ROLE_ISAT_COMPLIANT = 'hawaii.edu:store:uhims:general:isat-compliant';
const ROLE_ISAT_RENEWING = 'hawaii.edu:store:uhims:general:isat-compliant-renew'; // will first appear within 30 days of expiration

const MODAL_LOGGED_IN = 'MODAL_LOGGED_IN';
const MODAL_ISAT = 'MODAL_ISAT';

const DB_SDM = 'SDM';

// our intended structure for the WAT session var, we need these in place if we're going to
// watch our properties and be reactive to them. e.g. computed fields
const WAT_DEFAULT_SESSION = {
  canEdit: [],
  canView: [],
  campuses: [],
  canFinalize: [],
  accessManagement: [],
  bannerMessage: {
    show: false,
    type: 'warning',
    message: '',
    offset: 80 // calculated based on the height of the banner
  }
};

/**
 * Checks the uhReleasedGrouping array in the passed in state to see if the specified role exists for the session.
 */
function hasRole(stateParam, roleName) {
  return (_.has(stateParam, 'session.uhReleasedGrouping')) ? stateParam.session.uhReleasedGrouping.includes((roleName || '')) : false;
}

/**
 * Parse out the user type and try to get the campus they are affiliated with.
 * Priority
 *
 * - Faculty/Staff
 * - Student Employment
 * - uhOrgAffiliation.eduPersonOrgDN
 * - studentEmployee campus
 */
function parseAnalyticData(stateParam) {
  let userType = 'public';
  let campus = null;

  if (_.has(stateParam, 'session.uhReleasedGrouping')) {
    if (stateParam.session.uhReleasedGrouping.includes(ROLE_FACULTY_STAFF)) {
      userType = 'facstaff';
    }
    else {
      const regExp = new RegExp('[a-zA-Z]+:studentEmployee$', 'gm');
      const matches = _.filter(stateParam.session.uhReleasedGrouping, group => regExp.test(group));
      const firstMatch = _.first(matches);
      if (firstMatch) {
        const values = _.first(firstMatch.match(regExp)).split(':');
        campus = values[0];
        userType = values[1];
      }
    }
  }

  // if no campus yet, try to get it from uhOrgAffiliation
  if (!campus && _.has(stateParam, 'session.uhOrgAffiliation')) {
    const values = stateParam.session.uhOrgAffiliation.split(',');
    const key = 'eduPersonOrgDN=';
    const eduPersonOrgDN = _.find(values, value => value.startsWith(key));
    if (eduPersonOrgDN) {
      campus = eduPersonOrgDN.replace(key, '');
    }
  }

  return {
    userType,
    campus: campus || 'none'
  };
}

export const state = {
  displayMode: DISPLAY_MODE_LIGHT,
  isLoggedIn: false,
  isValidated: false,
  isSideBarOpen: false,
  session: {},
  appRoles: [],
  sessionWat: _.cloneDeep(WAT_DEFAULT_SESSION),

  // these are to keep track of whether or not it was seen
  // when the client refreshes the auth token, it will trigger an authenticated event so dont show again
  shownModal: {
    MODAL_LOGGED_IN: false,
    MODAL_ISAT: false
  },
  windowWidth: window.innerWidth,
  userPrefs: {},
  dbStatus: {
    SDM: true
  }
};

export const getters = {
  session: stateParam => stateParam.session,
  appRoles: stateParm => stateParm.appRoles,
  sessionWat: stateParam => stateParam.sessionWat,
  sessionHasToken: stateParam => _.has(stateParam, 'session.access_token'),
  sessionDisplayMode: stateParam => stateParam.displayMode,
  sessionDisplayIsDark: stateParam => stateParam.displayMode === DISPLAY_MODE_DARK,
  sessionDisplayIsLight: stateParam => stateParam.displayMode === DISPLAY_MODE_LIGHT,
  sessionDisplayIsSystem: stateParam => stateParam.displayMode === DISPLAY_MODE_SYSTEM,
  sessionGetDisplayClass: stateParam => (className) => {
    const suffix = stateParam.displayMode === 'light' ? '' : `-${stateParam.displayMode}`;
    // if (suffix === 'light') {
    //   return '';
    // }
    if (_.isArray(className)) {
      return _.reduce(className, (newClass, classs) => {
        return `${newClass} ${classs}${suffix}`;
      }, '');
    }
    else {
      return `${className}${suffix}`;
    }
  },
  sessionHasRole: stateParam => role => hasRole(stateParam, role),
  sessionIsSideBarOpen: stateParam => stateParam.isSideBarOpen,
  sessionIsLoggedIn: stateParam => !_.isEmpty(stateParam.session) && stateParam.isValidated,
  sessionIsValidated: stateParam => stateParam.isValidated,
  sessionIsFacultyStaff: stateParam => hasRole(stateParam, ROLE_FACULTY_STAFF),
  sessionFullName: stateParam => {
    if (_.has(stateParam, 'session.name')) {
      return stateParam.session.name;
    }
    else if (_.has(stateParam, 'session.preferred_username')) {
      return stateParam.session.preferred_username;
    }
    else {
      return GUEST_NAME;
    }
  },
  sessionProperty: stateParam => propKey => (_.has(stateParam, `session.${propKey}`) ? stateParam.session[propKey] : null),
  sessionShownLoggedInModal: stateParam => stateParam.shownModal[MODAL_LOGGED_IN],
  sessionShownISatModal: stateParam => stateParam.shownModal[MODAL_ISAT],
  sessionUsername: stateParam => (_.has(stateParam, 'session.username') ? stateParam.session.username : GUEST_NAME),
  sessionWebToken: stateParam => (_.has(stateParam, 'session.access_token') ? stateParam.session.access_token : null),
  sessionIsGCNCompliant: stateParam => hasRole(stateParam, ROLE_GCN_COMPLIANT),
  sessionIsISatCompliant: stateParam => hasRole(stateParam, ROLE_ISAT_COMPLIANT),
  sessionISatIsExpiring: stateParam => hasRole(stateParam, ROLE_ISAT_RENEWING),
  sessionWindowWidth: stateParam => stateParam.windowWidth,
  sessionSideBarWidth: stateParam => (stateParam.windowWidth <= util.MOBILE_WIDTH ? Math.min(stateParam.windowWidth * DISPLAY_PCT, util.MAX_SIDEBAR_WIDTH) : util.SIDEBAR_WIDTH),
  sessionSideBarShouldShiftContent: stateParam => stateParam.windowWidth > util.MOBILE_WIDTH,
  sessionGetRoleForApp: stateParam => (appid) => {
    return stateParam.appRoles[appid];
  },
  sessionUserPrefs: stateParam => stateParam.userPrefs,
  sessionAnalyticData: stateParam => parseAnalyticData(stateParam),
  sessionSDMUp: (stateParam) => {
    return stateParam.dbStatus[DB_SDM];
  }
};

export const mutations = {
  setDisplayMode: (stateParam, displayMode) => {
    stateParam.displayMode = displayMode;
    storage.setItem(storage.DISPLAY_MODE, stateParam.displayMode);
  },
  setIsSideBarOpen: (stateParam, flag) => {
    stateParam.isSideBarOpen = flag;
  },
  setIsValidated: (stateParam, flag) => {
    stateParam.isValidated = flag;
  },
  setLoggedIn: (stateParam, flag) => {
    stateParam.isLoggedIn = flag;

    // remove the token from local storage if logging out
    // if (!flag) {
    //   storage.removeItem(storage.SESSION_TOKEN);
    // }
  },
  setSession: (stateParam, session) => {
    // reset the session var to the default and copy in properties to preserve our structure
    stateParam.session = {};
    _.assignIn(stateParam.session, session);

    // set whether or not they are staff in localstorage; so if they never login again, we still know
    // only do it when logging in though
    if (!_.isEmpty(stateParam.session)) {
      storage.setItem(storage.IS_IRAPO, hasRole(stateParam, util.ROLE_IRAPO_STAFF));
    }
  },
  setSessionWat: (stateParam, sessionWat) => {
    // reset the sessionWat var to the default and copy in properties to preserve our structure
    stateParam.sessionWat = _.cloneDeep(WAT_DEFAULT_SESSION);
    _.assignIn(stateParam.sessionWat, sessionWat);
  },
  setShownLoggedInModal: (stateParam, value) => {
    stateParam.shownModal[MODAL_LOGGED_IN] = value;
  },
  setShownISatModal: (stateParam, value) => {
    stateParam.shownModal[MODAL_ISAT] = value;
  },
  setWindowWidth: (stateParam) => {
    stateParam.windowWidth = window.innerWidth;
  },
  setAppRoles: (stateParam, appRoles) => {
    _.remove(stateParam.appRoles, {id: appRoles.id})
    stateParam.appRoles.push(appRoles);
  },
  setUserPrefs: (stateParam, value) => {
    stateParam.userPrefs = value;
  },
  setSessionWatTemplateRoles: (stateParam, reports) => {
    // reports is an array of objects with template id and role
    if (reports.forEach) {

      // add the template id to the appropriate array
      reports.forEach(report => {
        if (report.role === watUtil.ROLE_SUBMITTER && !_.includes(stateParam.sessionWat.canEdit, report.id)) {
          stateParam.sessionWat.canEdit.push(report.id);
        }
        else if (report.role === watUtil.ROLE_VIEWER && !_.includes(stateParam.sessionWat.canView, report.id)) {
          stateParam.sessionWat.canView.push(report.id);
        }
        else if ((report.role === watUtil.ROLE_ADMIN || report.role === watUtil.ROLE_REVIEWER) && !_.includes(stateParam.sessionWat.canFinalize, report.id)) {
          stateParam.sessionWat.canFinalize.push(report.id);
        }
      });
    }
  },
  setSessionDBSDMUp: (stateParam, status) => {
    stateParam.dbStatus[DB_SDM] = status;
  }
};

/* Actions should always return a promise */
export const actions = {
  logout: async ({ commit }) => {
    commit('setLoggedIn', false);
    commit('setIsValidated', false);
    commit('setSession', {});
  },
  setSession: async ({ commit }, session) => {
    commit('setLoggedIn', true);
    commit('setSession', session);
  },
  setSessionWat: async ({ commit }, sessionWat) => {
    commit('setSessionWat', sessionWat);
  },
  setDisplayMode: async ({ commit }, displayMode) => {
    commit('setDisplayMode', displayMode);
  },
  setSessionIsSideBarOpen: async ({ commit }, isOpen) => {
    commit('setIsSideBarOpen', isOpen);
  },
  setSessionIsValidated: async ({ commit }, isValidated) => {
    commit('setIsValidated', isValidated);
  },
  setSessionShownLoggedInModal: async ({ commit }, value) => {
    commit('setShownLoggedInModal', value);
  },
  setSessionShownISatModal: async ({ commit }, value) => {
    commit('setShownISatModal', value);
  },
  setSessionWindowWidth: async ({ commit }) => {
    commit('setWindowWidth');
  },
  setSessionAppRoles: async ({ commit }, value) => {
    commit('setAppRoles', value);
  },
  setSessionUserPrefs: async ({ commit }, value) => {
    commit('setUserPrefs', value);
  },
  setSessionWatTemplateRoles: async ({ commit }, reports) => {
    commit('setSessionWatTemplateRoles', reports);
  },
  setSessionDBSDMUp: async ({ commit }, value) => {
    commit('setSessionDBSDMUp', value);
  }
};

export default { state, getters, mutations, actions, namespaced: true };
